/**
 * Sign-in page — supports three providers, wired to the license-server
 * admin-auth routes:
 *
 *   Password     POST /admin/login                  (primary)
 *   Google OIDC  POST /admin/oidc/callback          (optional; VITE_GOOGLE_CLIENT_ID)
 *   Dev session  POST /admin/dev-session            (non-prod, ?dev=1)
 *
 * All three return the same three-state shape:
 *   - `signed_in`                       → store session, go home
 *   - `totp_enrollment_required`        → render QR / otpauth prompt
 *   - `totp_challenge_required`         → render 6-digit prompt
 *
 * On any /internal API 401 the rest of the app clears the session and
 * bounces back here (see session.tsx onUnauthorized hook).
 */

import { useEffect, useState, useRef, type JSX } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { ShieldCheck, Eye, EyeOff } from 'lucide-react';
import { useSession, LOGOUT_UNCONFIRMED_KEY, type SessionUser } from '../session';
import { AuthShell } from '../components/shell/AuthShell';

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize(opts: { client_id: string; callback: (r: { credential: string }) => void }): void;
          renderButton(el: HTMLElement, opts: { theme: string; size: string }): void;
        };
      };
    };
  }
}

type Role = 'owner' | 'admin' | 'support' | 'readonly';
type AuthResponse =
  | { state: 'signed_in'; email: string; role: Role; token: string }
  | { state: 'totp_enrollment_required'; email: string; role: 'owner' | 'admin'; partial_session: string }
  | { state: 'totp_challenge_required'; email: string; role: 'owner' | 'admin'; partial_session: string };

const GOOGLE_CLIENT_ID =
  (import.meta as unknown as { env?: { VITE_GOOGLE_CLIENT_ID?: string } })
    .env?.VITE_GOOGLE_CLIENT_ID ?? '';

/** Operator-facing text for the API's machine error codes on the second-factor step. */
export function totpErrorMessage(code: string | undefined, status: number): string {
  switch (code) {
    case 'invalid_totp_code': return 'That code is not correct. Check your authenticator app and try again.';
    case 'rate_limited': return 'Too many attempts. Wait a minute and try again.';
    case 'account_locked': return 'This account is temporarily locked after too many failed attempts. Try again later.';
    case 'no_partial_session': case 'token_expired': return 'This sign-in expired. Go back and sign in again.';
    default: return code ?? `Sign-in failed (status ${status})`;
  }
}

export function SignInPage(): JSX.Element {
  const { signIn } = useSession();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const devMode = sp.get('dev') === '1';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [pending, setPending] = useState<AuthResponse | null>(null);
  const [logoutUnconfirmed] = useState<boolean>(() => {
    try {
      return sessionStorage.getItem(LOGOUT_UNCONFIRMED_KEY) === '1';
    } catch { return false; }
  });
  useEffect(() => { try { sessionStorage.removeItem(LOGOUT_UNCONFIRMED_KEY); } catch { /* ignore */ } }, []);
  const btnRef = useRef<HTMLDivElement | null>(null);

  const handleResponse = (r: AuthResponse): void => {
    if (r.state === 'signed_in') {
      signIn({ user_id: 'server-issued', email: r.email, role: r.role, mfa_ok: false }, r.token);
      stampSessionGreeting(r.email, r.role);
      nav('/');
    } else {
      setPending(r);
    }
  };

  const onPasswordSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      const res = await fetch('/admin/login', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), password }),
      });
      const body = await res.json() as AuthResponse | { error: string; retry_after_seconds?: number };
      if (!res.ok) {
        const msg = (body as { error: string }).error === 'account_locked'
          ? `Too many failed attempts — try again in ${(body as { retry_after_seconds?: number }).retry_after_seconds ?? 0}s`
          : (body as { error: string }).error === 'invalid_credentials'
          ? 'Wrong email or password'
          : (body as { error: string }).error === 'rate_limited'
          ? 'Too many attempts — slow down'
          : (body as { error: string }).error;
        throw new Error(msg);
      }
      handleResponse(body as AuthResponse);
    } catch (ex) {
      setErr(ex instanceof Error ? ex.message : String(ex));
    } finally {
      setBusy(false);
    }
  };

  // ── Google OIDC bootstrap (only when VITE_GOOGLE_CLIENT_ID is set) ──
  useEffect(() => {
    if (devMode || !GOOGLE_CLIENT_ID) return;
    const existing = document.getElementById('google-gsi-script');
    const setup = (): void => {
      window.google?.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: (resp) => { void handleIdToken(resp.credential); },
      });
      if (btnRef.current && window.google) {
        window.google.accounts.id.renderButton(btnRef.current, { theme: 'outline', size: 'large' });
      }
    };
    if (!existing) {
      const s = document.createElement('script');
      s.src = 'https://accounts.google.com/gsi/client';
      s.async = true; s.defer = true; s.id = 'google-gsi-script';
      document.head.appendChild(s);
      s.onload = setup;
    } else {
      setup();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [devMode]);

  const handleIdToken = async (idToken: string): Promise<void> => {
    setErr(null);
    try {
      const res = await fetch('/admin/oidc/callback', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ id_token: idToken }),
      });
      const body = await res.json() as AuthResponse | { error: string };
      if (!res.ok) throw new Error((body as { error: string }).error);
      handleResponse(body as AuthResponse);
    } catch (ex) {
      setErr(ex instanceof Error ? ex.message : String(ex));
    }
  };

  const devSignIn = async (): Promise<void> => {
    setErr(null);
    try {
      const res = await fetch('/admin/dev-session', { method: 'POST' });
      if (!res.ok) throw new Error(`dev-session failed: ${res.status}`);
      const body = await res.json() as {
        token: string; user: SessionUser;
      };
      signIn(body.user, body.token);
      stampSessionGreeting(body.user.email, body.user.role);
      nav('/');
    } catch (ex) {
      setErr(ex instanceof Error ? ex.message : String(ex));
    }
  };

  if (pending && pending.state !== 'signed_in') {
    return (
      <TotpPrompt
        pending={pending}
        onDone={(user, token) => {
          signIn(user, token);
          stampSessionGreeting(user.email, user.role);
          nav('/');
        }}
      />
    );
  }

  return (
    <AuthShell
      title="Vloud Admin"
      subtitle={
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <ShieldCheck width={12} height={12} />
          Sign in to manage licenses, customers, and the engine fleet.
        </span>
      }
      footer={<>Internal tool · MFA enforced for owner + admin roles</>}
    >
      <form onSubmit={(e) => void onPasswordSubmit(e)} style={{ display: 'grid', gap: 12 }}>
        {logoutUnconfirmed && (
          <div role="alert" data-testid="logout-unconfirmed">
            You were signed out here, but the server could not confirm that your previous session was ended.
            It may remain valid until it expires. On a shared computer, close the browser.
          </div>
        )}
        <label style={{ display: 'grid', gap: 4 }}>
          <span style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 500 }}>Email</span>
          <input type="email" value={email} required autoFocus
                 onChange={(e) => setEmail(e.target.value)}
                 disabled={busy}
                 autoComplete="email"
                 placeholder="you@yourcompany.com" />
        </label>
        <label style={{ display: 'grid', gap: 4 }}>
          <span style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 500 }}>Password</span>
          <div style={{ position: 'relative', display: 'flex' }}>
            <input
              type={showPw ? 'text' : 'password'}
              value={password} required
              onChange={(e) => setPassword(e.target.value)}
              disabled={busy}
              autoComplete="current-password"
              placeholder="••••••••••••"
              style={{ flex: 1, paddingRight: 36 }}
            />
            <button
              type="button"
              onClick={() => setShowPw((v) => !v)}
              aria-label={showPw ? 'Hide password' : 'Show password'}
              style={{
                position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)',
                background: 'transparent', border: 'none',
                color: 'var(--text-muted)', cursor: 'pointer',
                padding: 6, borderRadius: 6,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              {showPw ? <EyeOff width={14} height={14} /> : <Eye width={14} height={14} />}
            </button>
          </div>
        </label>
        <button type="submit" className="primary"
                disabled={busy || !email || !password}>
          {busy ? 'Signing in…' : 'Sign in'}
        </button>
      </form>

      <div style={{ marginTop: 10, fontSize: 13, textAlign: 'center' }}>
        <Link to="/forgot-password" style={{ color: 'var(--accent-text)' }}>Forgot password?</Link>
      </div>

      {(GOOGLE_CLIENT_ID || devMode) && (
        <>
          <div className="auth-divider">or</div>
          {GOOGLE_CLIENT_ID && !devMode && (
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <div ref={btnRef} />
            </div>
          )}
          {devMode && (
            <button onClick={() => void devSignIn()} style={{ width: '100%' }}>
              Continue as dev owner
            </button>
          )}
        </>
      )}

      {err && <p className="err" style={{ marginTop: 14 }}>{err}</p>}
    </AuthShell>
  );
}

function TotpPrompt({ pending, onDone }: {
  pending: Extract<AuthResponse, { state: 'totp_enrollment_required' | 'totp_challenge_required' }>;
  onDone: (user: SessionUser, token: string) => void;
}): JSX.Element {
  const [otpauthUrl, setOtpauthUrl] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (pending.state === 'totp_enrollment_required') {
      void fetch('/admin/totp/enroll', {
        method: 'POST',
        headers: { authorization: `Bearer ${pending.partial_session}` },
      })
        .then((r) => r.json())
        .then((b: { otpauth_url: string }) => setOtpauthUrl(b.otpauth_url))
        .catch((e) => setErr(String(e)));
    }
  }, [pending]);

  const verify = async (override?: string): Promise<void> => {
    const value = override ?? code;
    if (value.length !== 6) return;
    setBusy(true);
    setErr(null);
    try {
      const res = await fetch('/admin/totp/verify', {
        method: 'POST',
        headers: { authorization: `Bearer ${pending.partial_session}`, 'content-type': 'application/json' },
        body: JSON.stringify({ code: value }),
      });
      const body = await res.json() as { state: string; token?: string; error?: string };
      if (!res.ok) throw new Error(totpErrorMessage(body.error, res.status));
      if (!body.token) throw new Error('no token in response');
      onDone({ user_id: 'server-issued', email: pending.email, role: pending.role, mfa_ok: true }, body.token);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
      setCode('');
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell
      icon={ShieldCheck}
      title="Two-factor authentication"
      subtitle={
        pending.state === 'totp_enrollment_required'
          ? 'Scan this QR code with your authenticator app, then enter the 6-digit code to finish enrollment.'
          : 'Enter the 6-digit code from your authenticator app.'
      }
      footer={
        <Link to="/signin" style={{ color: 'var(--text-muted)' }}>
          ← Back to sign in
        </Link>
      }
    >
      {pending.state === 'totp_enrollment_required' && otpauthUrl && (
        <>
          <div className="qr-frame">
            <QRCodeSVG value={otpauthUrl} size={200} level="M" marginSize={2} />
          </div>
          <details className="otpauth-fallback">
            <summary>Can't scan? Show the raw setup URL</summary>
            <pre>{otpauthUrl}</pre>
          </details>
        </>
      )}

      <DigitBoxes
        value={code}
        onChange={setCode}
        onComplete={(v) => void verify(v)}
        disabled={busy}
      />

      <button
        className="primary"
        onClick={() => void verify()}
        disabled={code.length !== 6 || busy}
        style={{ marginTop: 14, width: '100%' }}
      >
        {busy ? 'Verifying…' : 'Verify'}
      </button>
      {err && <p className="err" style={{ marginTop: 14 }}>{err}</p>}
    </AuthShell>
  );
}

/* ──────────────────────────────────────────────────────────────────
 * 6-digit OTP input — six single-digit boxes that auto-advance and
 * support backspace/paste. Triggers `onComplete(code)` once filled.
 * ────────────────────────────────────────────────────────────────── */

function DigitBoxes({
  value, onChange, onComplete, disabled,
}: {
  value: string;
  onChange: (v: string) => void;
  onComplete: (v: string) => void;
  disabled?: boolean;
}): JSX.Element {
  const refs = useRef<Array<HTMLInputElement | null>>([]);

  useEffect(() => {
    // Auto-focus the first empty box on mount.
    refs.current[Math.min(value.length, 5)]?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (value.length === 6) onComplete(value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const onDigitChange = (i: number, raw: string): void => {
    // Strip non-digits; if pasted, accept up to 6.
    const cleaned = raw.replace(/[^0-9]/g, '');
    if (cleaned.length === 0) {
      // Backspace cleared this slot — leave the rest as-is.
      const next = value.slice(0, i) + value.slice(i + 1);
      onChange(next);
      return;
    }
    if (cleaned.length === 1) {
      const next = (value.slice(0, i) + cleaned + value.slice(i + 1)).slice(0, 6);
      onChange(next);
      refs.current[Math.min(i + 1, 5)]?.focus();
      return;
    }
    // Pasted multi-digit: distribute starting at index i.
    const merged = (value.slice(0, i) + cleaned).slice(0, 6);
    onChange(merged);
    refs.current[Math.min(merged.length, 5)]?.focus();
  };

  const onKeyDown = (i: number, e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Backspace' && !value[i] && i > 0) {
      e.preventDefault();
      const next = value.slice(0, i - 1) + value.slice(i);
      onChange(next);
      refs.current[i - 1]?.focus();
    } else if (e.key === 'ArrowLeft' && i > 0) {
      e.preventDefault();
      refs.current[i - 1]?.focus();
    } else if (e.key === 'ArrowRight' && i < 5) {
      e.preventDefault();
      refs.current[i + 1]?.focus();
    }
  };

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(6, 1fr)',
        gap: 8,
        marginTop: 14,
      }}
    >
      {Array.from({ length: 6 }).map((_, i) => (
        <input
          key={i}
          ref={(el) => { refs.current[i] = el; }}
          value={value[i] ?? ''}
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={1}
          autoComplete="one-time-code"
          aria-label={`Digit ${i + 1} of 6`}
          disabled={disabled}
          onChange={(e) => onDigitChange(i, e.target.value)}
          onKeyDown={(e) => onKeyDown(i, e)}
          onPaste={(e) => {
            const text = e.clipboardData.getData('text');
            if (/\d/.test(text)) {
              e.preventDefault();
              onDigitChange(i, text);
            }
          }}
          style={{
            width: '100%',
            aspectRatio: '1 / 1',
            textAlign: 'center',
            fontSize: '1.5rem',
            fontWeight: 600,
            fontFamily: 'ui-monospace, "JetBrains Mono", monospace',
            padding: 0,
            borderRadius: 10,
            border: '1px solid var(--border)',
            background: 'var(--bg-overlay)',
            color: 'var(--text-primary)',
            outline: 'none',
            transition: 'border-color 120ms ease',
          }}
          onFocus={(e) => {
            (e.currentTarget as HTMLInputElement).style.borderColor = 'var(--accent)';
            (e.currentTarget as HTMLInputElement).select();
          }}
          onBlur={(e) => {
            (e.currentTarget as HTMLInputElement).style.borderColor = 'var(--border)';
          }}
        />
      ))}
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────
 * Session greeting: stash a short banner payload right after sign-in.
 * The Dashboard page reads `vloud:session_greeting` on first render
 * and shows a transient "Welcome back" hint.
 * ────────────────────────────────────────────────────────────────── */

function stampSessionGreeting(email: string, role: string): void {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.setItem(
      'vloud:session_greeting',
      JSON.stringify({ email, role, at: Date.now() }),
    );
  } catch {
    // sessionStorage may be disabled in some embedded contexts; ignore.
  }
}
